import React, { useState } from 'react';
import {
  useLists, createList, deleteList, saveListEntries, newListEntry,
} from '../data/firestore';

// Lists for one space. Three flavors:
//   furniture — name/link/price/qty with an idea → ordered → delivered pipeline
//   todo      — checkboxes
//   shopping  — checkboxes with price/qty
// All share one storage shape (entries[] on the list doc).

const LIST_TYPES = [
  { id: 'furniture', label: 'Furniture & fixtures' },
  { id: 'todo', label: 'To-do' },
  { id: 'shopping', label: 'Shopping' },
];

const FURNITURE_STATUSES = ['idea', 'shortlisted', 'ordered', 'delivered'];
const STATUS_COLORS = { idea: '#9aa0a6', shortlisted: '#8ab4f8', ordered: '#ffd166', delivered: '#7ce38b' };

const fmtMoney = (n) =>
  n ? `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '';

function EntryRow({ entry, type, onPatch, onRemove, canEdit }) {
  const isFurniture = type === 'furniture';
  return (
    <div className="ah-list-row">
      {!isFurniture && (
        <input
          type="checkbox"
          checked={!!entry.done}
          disabled={!canEdit}
          onChange={(e) => onPatch({ done: e.target.checked })}
        />
      )}
      <input
        className="cs-input ah-list-name"
        style={entry.done ? { textDecoration: 'line-through', opacity: 0.55 } : undefined}
        value={entry.name}
        placeholder={isFurniture ? 'Piece — e.g. bed frame, sconces…' : 'Task / item…'}
        readOnly={!canEdit}
        onChange={(e) => onPatch({ name: e.target.value })}
      />
      {(isFurniture || type === 'shopping') && (
        <>
          <input
            className="cs-input ah-list-price"
            type="number"
            min="0"
            placeholder="$"
            value={entry.price || ''}
            readOnly={!canEdit}
            onChange={(e) => onPatch({ price: Number(e.target.value) || 0 })}
          />
          <input
            className="cs-input ah-list-qty"
            type="number"
            min="1"
            title="Quantity"
            value={entry.qty || 1}
            readOnly={!canEdit}
            onChange={(e) => onPatch({ qty: Math.max(1, Number(e.target.value) || 1) })}
          />
        </>
      )}
      {isFurniture && (
        <select
          className="cs-input ah-list-status"
          value={entry.status || 'idea'}
          style={{ color: STATUS_COLORS[entry.status || 'idea'] }}
          disabled={!canEdit}
          onChange={(e) => onPatch({ status: e.target.value })}
        >
          {FURNITURE_STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      )}
      <input
        className="cs-input ah-list-link"
        placeholder="link"
        value={entry.link || ''}
        readOnly={!canEdit}
        onChange={(e) => onPatch({ link: e.target.value })}
      />
      {entry.link && (
        <a href={entry.link} target="_blank" rel="noreferrer" className="cs-btn cs-btn-ghost cs-btn-sm" title="Open link">
          ↗
        </a>
      )}
      {canEdit && <button className="cs-btn cs-btn-ghost cs-btn-sm" title="Remove" onClick={onRemove}>✕</button>}
    </div>
  );
}

function ListCard({ spaceId, list, canEdit }) {
  const entries = list.entries || [];
  const save = (next) => saveListEntries(spaceId, list.id, next);
  const patchEntry = (id, patch) => save(entries.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  const removeEntry = (id) => save(entries.filter((e) => e.id !== id));

  const doneCount = list.type === 'furniture'
    ? entries.filter((e) => e.status === 'delivered').length
    : entries.filter((e) => e.done).length;
  const total = entries.reduce((s, e) => s + (Number(e.price) || 0) * (Number(e.qty) || 1), 0);

  return (
    <div className="cs-card" style={{ padding: 16, marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>{list.title}</h3>
        <span className="cs-help" style={{ fontSize: 12 }}>
          {LIST_TYPES.find((t) => t.id === list.type)?.label || list.type} · {doneCount}/{entries.length}
          {total > 0 && ` · ${fmtMoney(total)}`}
        </span>
        <span style={{ flex: 1 }} />
        {canEdit && (
          <button
            className="cs-btn cs-btn-ghost cs-btn-sm"
            onClick={() => {
              // eslint-disable-next-line no-alert
              if (window.confirm(`Delete list “${list.title}”?`)) deleteList(spaceId, list.id);
            }}
          >
            Delete
          </button>
        )}
      </div>

      <div style={{ marginTop: 10 }}>
        {entries.map((e) => (
          <EntryRow
            key={e.id}
            entry={e}
            type={list.type}
            canEdit={canEdit}
            onPatch={(patch) => patchEntry(e.id, patch)}
            onRemove={() => removeEntry(e.id)}
          />
        ))}
      </div>

      {canEdit && (
        <button
          className="cs-btn cs-btn-outline cs-btn-sm"
          style={{ marginTop: 10 }}
          onClick={() => save([...entries, newListEntry()])}
        >
          + Add row
        </button>
      )}
    </div>
  );
}

export default function SpaceLists({ space, canEdit = true }) {
  const { docs: lists } = useLists(space.id);
  const [title, setTitle] = useState('');
  const [type, setType] = useState('furniture');

  return (
    <div>
      {canEdit && (
        <form
          style={{ display: 'flex', gap: 8, flexWrap: 'wrap', maxWidth: 620 }}
          onSubmit={async (e) => {
            e.preventDefault();
            if (!title.trim()) return;
            await createList(space.id, { title: title.trim(), type });
            setTitle('');
          }}
        >
          <input
            className="cs-input"
            style={{ flex: '2 1 220px' }}
            placeholder="New list — e.g. “Bedroom 2 furniture”…"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <select className="cs-input" style={{ flex: '1 1 140px' }} value={type} onChange={(e) => setType(e.target.value)}>
            {LIST_TYPES.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
          <button className="cs-btn cs-btn-primary" type="submit" disabled={!title.trim()}>
            Create
          </button>
        </form>
      )}

      {lists === null && <p className="cs-help" style={{ marginTop: 14 }}>Loading…</p>}
      {lists && !lists.length && (
        <div className="cs-empty" style={{ marginTop: 14 }}>
          {canEdit ? 'No lists yet — start a furniture list, a to-do list or a shopping list.' : 'No lists yet.'}
        </div>
      )}
      {(lists || []).map((l) => (
        <ListCard key={l.id} spaceId={space.id} list={l} canEdit={canEdit} />
      ))}
    </div>
  );
}
